import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

/**
 * Video controlado por el scroll: el avance del scroll sobre `triggerRef`
 * mueve el currentTime del video (técnica "scroll-scrub").
 *
 * Uso:
 *   const sectionRef = useRef(null);
 *   const videoRef = useScrollVideo({ triggerRef: sectionRef });
 *   <section ref={sectionRef}><video ref={videoRef} muted playsInline preload="auto" /></section>
 *
 * El seek no se hace directo en cada evento de scroll: un rAF interpola
 * hacia el tiempo objetivo (lerp) para que la rueda del mouse no se sienta
 * a saltos, y el loop se detiene solo cuando llega al objetivo.
 *
 * El video tiene que venir con todos los cuadros como keyframe, si no cada
 * seek decodifica desde el keyframe anterior y el scroll se ve a saltos (el
 * metaball original, con un keyframe por segundo, tardaba hasta 55 ms por
 * seek; recodificado tarda unos pocos ms y pesa menos):
 *   ffmpeg -i in.mp4 -an -c:v libx264 -preset slow -crf 26 -g 1 -bf 0 -pix_fmt yuv420p -movflags +faststart out.mp4
 */
export default function useScrollVideo({ triggerRef, start = 'top top', end = 'bottom bottom', smoothing = 0.12 } = {}) {
    const videoRef = useRef(null);

    useGSAP(() => {
        const video = videoRef.current;
        const trigger = triggerRef?.current;
        if (!video || !trigger) return undefined;

        let target = 0;
        let current = 0;
        let rafId = null;

        const duration = () => (Number.isFinite(video.duration) ? video.duration : 0);

        const tick = () => {
            current += (target - current) * smoothing;
            if (Math.abs(target - current) < 0.0005) current = target;

            const d = duration();
            // Nunca pedir exactamente `duration`: algunos navegadores muestran negro en el último frame
            const desired = Math.min(current * d, d - 0.05);
            const synced = d === 0 || Math.abs(video.currentTime - desired) < 0.02;
            if (!synced && !video.seeking) video.currentTime = desired;

            // Seguir hasta que el scroll llegó Y el video también: si el último seek
            // coincidió con uno en curso, el frame final quedaría sin aplicar.
            rafId = current === target && synced ? null : requestAnimationFrame(tick);
        };

        // No llamarla `start`: taparía la opción `start` y ScrollTrigger recibiría la función
        const kick = () => {
            if (!rafId) rafId = requestAnimationFrame(tick);
        };

        const st = ScrollTrigger.create({
            trigger,
            start,
            end,
            onUpdate: (self) => {
                target = self.progress;
                kick();
            },
        });

        // Si el usuario llega con scroll antes de que carguen los metadatos
        video.addEventListener('loadedmetadata', kick);

        // iOS no permite hacer seek en un video que nunca se reprodujo
        video.pause();
        const unlock = video.play();
        if (unlock && typeof unlock.then === 'function') {
            unlock.then(() => video.pause()).catch(() => { });
        }

        return () => {
            st.kill();
            video.removeEventListener('loadedmetadata', kick);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, { dependencies: [triggerRef, start, end, smoothing] });

    return videoRef;
}
